import React, { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LLMSettings } from "@/components/LLMSettings";
import { toast } from "sonner";
import { ShieldCheck, Lock, User, At, ArrowsClockwise, Brain } from "@phosphor-icons/react";

export default function Account() {
    const { user, loginWithToken } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [newPwdConfirm, setNewPwdConfirm] = useState("");
    const [twofa, setTwoFa] = useState(null); // {totp_setup}
    const [twofaCode, setTwoFaCode] = useState("");
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);
    const [resetting2FA, setResetting2FA] = useState(false);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            const { data } = await api.patch("/auth/me", { name, email });
            toast.success("Perfil actualizado");
            if (data.token) loginWithToken(data.token, { email: data.email, name: data.name });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al guardar");
        } finally { setSavingProfile(false); }
    };

    const changePwd = async (e) => {
        e.preventDefault();
        if (newPwd !== newPwdConfirm) { toast.error("Las contraseñas nuevas no coinciden"); return; }
        if (newPwd.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
            toast.success("Contraseña actualizada");
            setCurrentPwd(""); setNewPwd(""); setNewPwdConfirm("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error");
        } finally { setSavingPwd(false); }
    };

    const start2FAReset = async () => {
        setResetting2FA(true);
        try {
            const { data } = await api.post("/auth/reset-2fa");
            setTwoFa(data);
            toast.info("Escanea el QR nuevo y confirma con un código");
        } catch (err) {
            toast.error("No se pudo iniciar el reset 2FA");
        } finally { setResetting2FA(false); }
    };

    const confirm2FAReset = async (e) => {
        e.preventDefault();
        try {
            await api.post("/auth/confirm-2fa-reset", { email, password: currentPwd || "x", totp_code: twofaCode });
            toast.success("2FA actualizado correctamente");
            setTwoFa(null); setTwoFaCode("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Código inválido");
        }
    };

    return (
        <div className="fade-up">
            <PageHeader eyebrow="Configuración" title="Mi cuenta"
                lead="Gestiona tus datos de acceso, contraseña, 2FA y el proveedor de inteligencia artificial." />
            <div className="px-8 md:px-12 py-10 max-w-3xl space-y-12">
                {/* Inteligencia Artificial */}
                <section data-testid="account-llm-section">
                    <div className="label-eyebrow mb-3 flex items-center gap-2"><Brain size={14}/> Inteligencia Artificial</div>
                    <LLMSettings />
                </section>

                {/* Perfil */}
                <section data-testid="account-profile-section">
                    <div className="label-eyebrow mb-3 flex items-center gap-2"><User size={14}/> Perfil</div>
                    <form onSubmit={saveProfile} className="border hairline bg-white p-6 space-y-4">
                        <Field label="Nombre"><input className="input-base" value={name} onChange={(e)=>setName(e.target.value)} data-testid="account-name-input"/></Field>
                        <Field label="Email"><input type="email" className="input-base" value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="account-email-input"/></Field>
                        <button className="btn-primary" disabled={savingProfile} data-testid="account-save-profile-button">{savingProfile ? "Guardando…" : "Guardar perfil"}</button>
                    </form>
                </section>

                {/* Cambiar contraseña */}
                <section data-testid="account-password-section">
                    <div className="label-eyebrow mb-3 flex items-center gap-2"><Lock size={14}/> Contraseña</div>
                    <form onSubmit={changePwd} className="border hairline bg-white p-6 space-y-4">
                        <Field label="Contraseña actual"><input type="password" className="input-base" value={currentPwd} onChange={(e)=>setCurrentPwd(e.target.value)} required data-testid="account-currentpwd-input"/></Field>
                        <Field label="Nueva contraseña (mín. 8)"><input type="password" className="input-base" value={newPwd} onChange={(e)=>setNewPwd(e.target.value)} required data-testid="account-newpwd-input"/></Field>
                        <Field label="Confirmar nueva contraseña"><input type="password" className="input-base" value={newPwdConfirm} onChange={(e)=>setNewPwdConfirm(e.target.value)} required data-testid="account-newpwd-confirm-input"/></Field>
                        <button className="btn-primary" disabled={savingPwd} data-testid="account-save-password-button">{savingPwd ? "Cambiando…" : "Cambiar contraseña"}</button>
                    </form>
                </section>

                {/* 2FA */}
                <section data-testid="account-2fa-section">
                    <div className="label-eyebrow mb-3 flex items-center gap-2"><ShieldCheck size={14}/> Autenticación 2FA</div>
                    <div className="border hairline bg-white p-6 space-y-4">
                        <p className="text-sm text-[var(--ink-2)]">
                            Resetea tu 2FA si has perdido el acceso a tu autenticador o quieres regenerar el código.
                            Generaremos un QR nuevo que deberás escanear con Google Authenticator.
                        </p>
                        {!twofa ? (
                            <button onClick={start2FAReset} disabled={resetting2FA} className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] flex items-center gap-2" data-testid="account-reset-2fa-button">
                                <ArrowsClockwise size={14}/> {resetting2FA ? "Generando…" : "Resetear 2FA"}
                            </button>
                        ) : (
                            <form onSubmit={confirm2FAReset} className="space-y-4">
                                <img src={twofa.totp_setup.qr_data_url} alt="Nuevo QR 2FA" className="w-44 h-44 mx-auto" data-testid="account-2fa-qr"/>
                                <div className="text-center text-xs"><code className="mono text-[var(--ink-2)]">{twofa.totp_setup.secret}</code></div>
                                <Field label="Código de 6 dígitos">
                                    <input className="input-base mono tracking-[0.4em] text-lg text-center" value={twofaCode} onChange={(e)=>setTwoFaCode(e.target.value.replace(/\D/g,""))} maxLength={6} required data-testid="account-2fa-code-input"/>
                                </Field>
                                <button className="btn-primary w-full" data-testid="account-confirm-2fa-button">Confirmar nuevo 2FA</button>
                            </form>
                        )}
                    </div>
                </section>
            </div>
            <style>{`.input-base{width:100%;padding:10px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none}.input-base:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>);
}
